import { useContext } from 'react';

import {
  faEllipsis,
  faMoneyCheckDollarPen,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowUpRightFromSquare,
  faVideo,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLingui } from '@lingui/react/macro';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/videa-components/src/index';
import { useSelector } from 'react-redux';

import { PmsType, type User } from '@/@types/global';
import routesConstants from '@/routes/routes.constants';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ExternalPatientContext } from '../constants';
import { IframeMessageType, ModalType, TrialStatus } from '../types';

interface Props {
  icon: IconProp;
  onClick: () => void;
  title: string;
}

const VisitSidebarDropdownMenuItem = ({ icon, onClick, title }: Props) => (
  <DropdownMenuItem
    className="hover:!vai-bg-zinc-700 hover:!vai-text-white vai-cursor-pointer"
    onClick={onClick}
  >
    <FontAwesomeIcon icon={icon} />
    {title}
  </DropdownMenuItem>
);

export const VisitSidebarMenu = ({
  onManageSubscription,
  trialStatus,
}: {
  onManageSubscription: () => void;
  trialStatus?: TrialStatus;
}) => {
  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();
  const { externalPatientId, externalImageId, imageId, patientId } = useContext(
    ExternalPatientContext,
  );
  const pmsType = useSelector(
    (state: { login?: { user?: User } }) =>
      state.login?.user?.practice?.pmsType,
  );

  const isPaidSubscription = trialStatus === TrialStatus.PAID;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="vai-bg-zinc-950 vai-text-white vai-border vai-border-zinc-800 vai-rounded-md vai-w-6 vai-h-6 vai-flex vai-items-center vai-justify-center data-[state=open]:vai-bg-zinc-800">
        <FontAwesomeIcon icon={faEllipsis} />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="vai-w-56 vai-bg-zinc-800 vai-text-white vai-border-none"
        align="end"
      >
        {/* TODO: Add back in when content is ready */}
        {/* {isPaidSubscription && (
          <VisitSidebarDropdownMenuItem
            title={t`AI Impact`}
            onClick={() => {
              trackEvent(mixpanelEvents.externalAiImpactModalOpen, {
                externalPatientId,
                externalImageId,
                patientId,
                imageId,
                source: 'MENU',
              });
              window.parent.postMessage(
                {
                  type: IframeMessageType.MODAL_OPEN,
                  payload: {
                    type: ModalType.AI_IMPACT,
                    patientId,
                    imageId,
                    externalPatientId,
                    externalImageId,
                  },
                },
                '*',
              );
            }}
            icon={faChartSimple}
          />
        )} */}
        <VisitSidebarDropdownMenuItem
          title={t`AI Onboarding`}
          onClick={() => {
            trackEvent(mixpanelEvents.externalOnboardingModalOpen, {
              externalPatientId,
              externalImageId,
              patientId,
              imageId,
              source: 'MENU',
            });
            window.parent.postMessage(
              {
                type: IframeMessageType.MODAL_OPEN,
                payload: {
                  type: ModalType.ONBOARDING,
                  patientId,
                  imageId,
                  externalPatientId,
                  externalImageId,
                },
              },
              '*',
            );
          }}
          icon={faVideo}
        />
        {/* Commenting out until we get permissions for this */}
        {/* {pmsType === PmsType.DENTRIX_ASCEND && (
          <VisitSidebarDropdownMenuItem
            title={t`Patient List`}
            onClick={() => {
              trackEvent(mixpanelEvents.externalPatientListClick, {
                externalPatientId,
                externalImageId,
                patientId,
                imageId,
                source: 'MENU',
              });
              window.open(routesConstants.UPLOADER, '_blank');
            }}
            icon={faArrowUpRightFromSquare}
          />
        )} */}
        {/* Commenting out after request from HS1 */}
        {/* <VisitSidebarDropdownMenuItem
          title={t`Manage Subscription`}
          onClick={onManageSubscription}
          icon={faMoneyCheckDollarPen}
        /> */}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
